import * as React from "react";
import * as ReactDOM from "react-dom";
import * as _data from "../../data";
import * as _helpers from "../../helpers";
import { customTags } from "../../helpers";
import { initPolyfills } from "../Polyfills";
import { ErrorBoundary } from "../../components";
/* Missing HTMLElement ref is "NULL" */
/* tslint:disable:no-null-keyword  no-non-null-assertion */
// make sure we have all the needed custom tags registered on the page
_helpers.registerCustomTagsFromTagsCollection(customTags);
// this is needed to make all the helpers available in the exported library
_helpers;
_data;
// make sure we support everything we need
initPolyfills();
/**
 * BaseBlock class provides interface for communication between newly developed blocks and SBTech Responsive Application.
 */
var BaseBlock = /** @class */ (function () {
    /**
     * @param {string} domId represent ID selector for an HTML element
     * @param {BaseBlockOptions} options providing interfaces for data, i18l, configuration
     */
    function BaseBlock(domId, options) {
        // error loggin for production code
        if (typeof domId !== "string") {
            throw new Error("Wrong type of \"domId\" -> " + typeof domId + ", expected to be of type STRING");
        }
        this.domId = domId;
        this.dataProvider = null;
        this.isActive = false;
        this.isCustomLoaderProcessing = false;
        this.isShown = false;
        this.navHandler = options && options.navHandler || null;
        this.navigationPath = "";
        this.onBlockReady = {};
        this.options = options;
        this.panelId = options && options.panelId || "";
        this.parentBlock = null;
    }
    // Ignoring React side effects during coverage tests
    /**
     * Activate method mounts react component instance to the DOM
     * Uses <ErrorBoundary /> component to wrap up components for propper error handling
     *
     * @param JSX.Element component
     * @returns BaseBlock instance
     */
    /* istanbul ignore next */
    BaseBlock.prototype.activate = function (component) {
        this.beforeActivate();
        var domRef = document.getElementById(this.domId);
        if (domRef !== null && typeof component !== "undefined") {
            var comp = React.createElement(ErrorBoundary, null, component);
            ReactDOM.render(comp, domRef);
            this.isActive = true;
        }
        this.afterActivate();
        return this;
    };
    // Ignoring React side effects during coverage tests
    /**
     * Deactivate method unmounts react component instance from the DOM
     * @returns BaseBlock instance
     */
    /* istanbul ignore next */
    BaseBlock.prototype.deactivate = function () {
        this.beforeDeactivate();
        var domRef = document.getElementById(this.domId);
        if (domRef !== null) {
            ReactDOM.unmountComponentAtNode(domRef);
            this.isActive = false;
        }
        if (this.dataProvider !== null && typeof this.dataProvider.deactivate === "function") {
            this.dataProvider.deactivate();
        }
        this.afterDeactivate();
        return this;
    };
    /**
     * afterActivate method is called after the react block is mounted in the DOM
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.afterActivate = function () {
        return this;
    };
    /**
     * afterDeactivate method is called after the react block is unmounted from the DOM
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.afterDeactivate = function () {
        return this;
    };
    /**
     * beforeActivate method is called before the react block is mounted in the DOM
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.beforeActivate = function () {
        return this;
    };
    /**
     * beforeDeactivate method is called before the react block is unmounted from the DOM
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.beforeDeactivate = function () {
        return this;
    };
    /**
     * getBlockContainer
     * @returns HTMLElement - element containing the react block
     */
    BaseBlock.prototype.getBlockContainer = function () {
        return document.getElementById(this.domId);
    };
    /**
     * getPanel returns panel when react block is inside the responsive application
     * @returns PanelInstance - not typed in the react lib
     */
    BaseBlock.prototype.getPanel = function () {
        // PanelID -> compatibility with mobile app
        return this.navHandler.Panels[this.panelId || this["PanelID"]];
    };
    /**
     * getPanelPosition position in the layout
     * @returns Position - not typed in the react lib
     */
    BaseBlock.prototype.getPanelPosition = function () {
        return this.getPanel().Position;
    };
    /**
     * getTabLabel method not used in the react blocks - ensures backward compatibility
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.getTabLabel = function () {
        return this;
    };
    /**
     * hidePreloader method not used in the react blocks - ensures backward compatibility
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.hidePreloader = function () {
        // if (this && !this.isCustomLoaderProcessing) {
        //     var preloader = document.getElementById('preloader');
        //     Dom.hideElement(preloader);
        // }
        return this;
    };
    /**
     * hideBlockLoader method not used in the react blocks - ensures backward compatibility
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.hideBlockLoader = function () {
        return this;
    };
    /**
     * init method not used in the react blocks - ensures backward compatibility
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.init = function () {
        return this;
    };
    /**
     * destroy clear all stored parameter. Block cannot be activated anymore once the instance is destroyed.
     * @returns BaseBlock instance with nullified fields
     */
    BaseBlock.prototype.destroy = function () {
        this.domId = "";
        this.panelId = "";
        this.isShown = false;
        this.isActive = false;
        this.options = undefined;
        this.navigationPath = "";
        this.navHandler = undefined;
        this.parentBlock = undefined;
        this.onBlockReady = undefined;
        this.dataProvider = undefined;
        this.isCustomLoaderProcessing = false;
        return this;
    };
    /**
     * isDesktop method not used in the react blocks - ensures backward compatibility
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.isDesktop = function () {
        // return Application.deviceType.isDesktop();
        return this;
    };
    /**
     * Switches view to another block
     *
     * @param {any[]} params Function arguments as array
     */
    BaseBlock.prototype.navigateTo = function () {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        if (params.length > 0) {
            for (var i = 0; i < params.length; i += 1) {
                if (params[i] === null || params[i] === undefined) {
                    params[i] = 0;
                }
                else if (!isNaN(params[i])) {
                    // Prevent parsing to number in blocks because Application navigation passes all parameters as string
                    params[i] = Number(params[i]);
                }
            }
            this.navHandler.navigateTo(this.panelId, this.getPanel().isSidePanel(), params, this.navigationPath);
            this.navigationPath === "";
        }
    };
    /**
     * onBlockHide method sets property isShown, called when block is hidden
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.onBlockHide = function () {
        this.isShown = false;
        return this;
    };
    /**
     * onBlockHide method sets property isShown called when block is shown
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.onBlockShow = function () {
        this.isShown = true;
        return this;
    };
    /**
     * onPanelEnter method called when user navigates to a panel
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.onPanelEnter = function () {
        return this;
    };
    /**
     * onPanelLeave method called when user navigates away from panel
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.onPanelLeave = function () {
        return this;
    };
    /**
     * Sets navigation path to block instance
     *
     * @param {string} path Navigation path to block instance
     */
    BaseBlock.prototype.setNavigationPath = function (path) {
        this.navigationPath = path;
        return this;
    };
    /**
     * Sets dynamic configurations of data provider for pulling data
     *
     * @param {any[]} params Function arguments as array
     */
    BaseBlock.prototype.setParams = function () {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        if (this.dataProvider !== null && typeof this.dataProvider.setParams === "function") {
            /* istanbul ignore next */
            this.dataProvider.setParams.apply(this.dataProvider, params);
        }
    };
    /**
     * showPreloader not used in react blocks, here for consistency
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.showPreloader = function () {
        // if (!this.isCustomLoaderProcessing) {
        //     var preloader = document.getElementById('preloader');
        //     Dom.showElement(preloader);
        // }
        return this;
    };
    /**
     * whenBlockIsReady no used in react blocks, here for consistency
     * @returns BaseBlock instance
     */
    BaseBlock.prototype.whenBlockIsReady = function () {
        // if ((this.htmlContainer || this.TabsContainerElem) && this.onBlockReady) {
        //     executeEvents(this.onBlockReady, this.ID,
        //         {
        //             htmlContainerID: this.constructor.name
        //         });
        // }
        return this;
    };
    return BaseBlock;
}());
export { BaseBlock };
//# sourceMappingURL=BaseBlock.js.map